export interface FormattedMetricValue {
  formattedValue: string;
  fullValue: string | null;
}

/**
 * Formats a metric value based on the specified format type.
 * For currency values >= 10K, abbreviates to K/M notation and returns the full value for tooltip display.
 */
export function formatMetricValue(
  value: string | number,
  format?: 'number' | 'currency' | 'percentage'
): FormattedMetricValue {
  if (typeof value === 'string') {
    return { formattedValue: value, fullValue: null };
  }

  if (format === 'currency') {
    const full = `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    if (value >= 1_000_000) {
      return {
        formattedValue: `$${(value / 1_000_000).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}M`,
        fullValue: full,
      };
    }
    if (value >= 10_000) {
      return {
        formattedValue: `$${(value / 1_000).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}K`,
        fullValue: full,
      };
    }
    return { formattedValue: full, fullValue: null };
  }

  if (format === 'percentage') {
    return { formattedValue: `${value}%`, fullValue: null };
  }

  return { formattedValue: value.toLocaleString(), fullValue: null };
}

/**
 * Builds tooltip text from an optional full value and description.
 */
export function buildMetricTooltip(fullValue: string | null | undefined, description?: string): string {
  return [fullValue, description].filter(Boolean).join(' — ');
}
